import { useState, type ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buildChildrenByParentId } from '@/lib/documentGroupTree';

export type MunicipalityDocumentGroupOption = {
  id: number;
  name: string;
  description?: string | null;
  position: number;
  parent_id?: number | null;
};

export type MunicipalityOption = {
  id: number;
  name: string;
  state?: string | null;
  /** Server: File Viewer uploads allowed for this municipality (team has :file_viewer Flipper) */
  file_viewer_access?: boolean;
  municipality_document_groups: MunicipalityDocumentGroupOption[];
};

export function municipalityLabel(municipality: Pick<MunicipalityOption, 'name' | 'state'>): string {
  const state = municipality.state?.trim();
  if (!state) return municipality.name;
  return `${municipality.name}, ${state}`;
}

export type MunicipalityLinkSelection = {
  municipalityId: number;
  folderId: string;
};

type MunicipalityLinkFieldsProps = {
  municipalities: MunicipalityOption[];
  documentType: string;
  links: MunicipalityLinkSelection[];
  onChange: (links: MunicipalityLinkSelection[]) => void;
  renderFolderActions?: (municipality: MunicipalityOption) => ReactNode;
};

export function municipalitiesForDocumentType(
  municipalities: MunicipalityOption[],
  documentType: string,
): MunicipalityOption[] | null {
  if (documentType === 'potential_state_law') return null;
  if (documentType === 'uploaded_file') {
    return municipalities.filter((municipality) => municipality.file_viewer_access === true);
  }
  return municipalities;
}

function folderSelectOptions(groups: MunicipalityDocumentGroupOption[]): { id: number; label: string }[] {
  const childrenByParent = buildChildrenByParentId(groups);
  const options: { id: number; label: string }[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const group of childrenByParent.get(parentId) ?? []) {
      options.push({ id: group.id, label: `${'\u2014 '.repeat(depth)}${group.name}` });
      walk(group.id, depth + 1);
    }
  };
  walk(null, 0);
  return options;
}

export default function MunicipalityLinkFields({
  municipalities,
  documentType,
  links,
  onChange,
  renderFolderActions,
}: MunicipalityLinkFieldsProps) {
  const [query, setQuery] = useState('');
  const choices = municipalitiesForDocumentType(municipalities, documentType);
  if (!choices) return null;

  const selectedIds = new Set(links.map((link) => link.municipalityId));
  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (municipality: MunicipalityOption) => (
    municipalityLabel(municipality).toLowerCase().includes(normalizedQuery)
  );
  const visible = choices.filter((municipality) => {
    if (selectedIds.has(municipality.id)) return true;
    if (normalizedQuery === '') return false;
    return matchesQuery(municipality);
  });
  const hasUnselectedMatch = normalizedQuery !== '' && choices.some((municipality) => (
    !selectedIds.has(municipality.id) && matchesQuery(municipality)
  ));

  const toggle = (municipalityId: number, checked: boolean) => {
    if (checked) {
      onChange([...links, { municipalityId, folderId: '' }]);
      return;
    }
    onChange(links.filter((link) => link.municipalityId !== municipalityId));
  };

  const setFolder = (municipalityId: number, folderId: string) => {
    onChange(links.map((link) => (
      link.municipalityId === municipalityId ? { ...link, folderId } : link
    )));
  };

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">Municipalities</legend>
      <p className="text-xs text-muted-foreground">
        {documentType === 'uploaded_file'
          ? 'Select every municipality this file belongs to. At least one is required.'
          : 'Optional. Link this document to one or more municipalities. Each can have its own folder.'}
      </p>
      {choices.length === 0 ? (
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {documentType === 'uploaded_file'
            ? 'File Center is not enabled for your account. Contact your Customer Success Manager to unlock this feature, then refresh this page.'
            : 'No municipalities are available.'}
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <Label htmlFor="municipality-search">Search municipalities</Label>
            <Input
              id="municipality-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name"
            />
          </div>
          {normalizedQuery === '' && (
            <p className="text-sm text-muted-foreground">Search for a municipality.</p>
          )}
          {normalizedQuery !== '' && !hasUnselectedMatch && (
            <p className="text-sm text-muted-foreground">
              {selectedIds.size > 0
                ? 'No other municipalities match that search.'
                : 'No municipalities match that search.'}
            </p>
          )}
          {visible.length > 0 && (
            <ul className="space-y-3">
              {visible.map((municipality) => {
                const selected = selectedIds.has(municipality.id);
                const link = links.find((item) => item.municipalityId === municipality.id);
                const label = municipalityLabel(municipality);
                const checkboxId = `municipality-${municipality.id}`;
                const folderId = `municipality-folder-${municipality.id}`;
                return (
                  <li key={municipality.id} className="space-y-2 rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={checkboxId}
                        checked={selected}
                        onCheckedChange={(checked) => toggle(municipality.id, checked === true)}
                      />
                      <Label htmlFor={checkboxId}>{label}</Label>
                    </div>
                    {selected && (
                      <div className="space-y-2 pl-6">
                        <Label htmlFor={folderId}>Folder for {label}</Label>
                        <select
                          id={folderId}
                          value={link?.folderId ?? ''}
                          onChange={(event) => setFolder(municipality.id, event.target.value)}
                          className="border rounded-md p-2 w-full"
                        >
                          <option value="">No folder assignment</option>
                          {folderSelectOptions(municipality.municipality_document_groups).map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {renderFolderActions?.(municipality)}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </fieldset>
  );
}
